import { z } from 'zod';
import { CreateGmailFilterActionSchema, FilterModelSchema } from './step-three';
import { AddEmailDelegateActionSchema } from './policy-actions/add-email-delegate';
import { AddSendAsActionSchema } from './policy-actions/add-send-as';
import { AddSharedDriveActionSchema } from './policy-actions/add-share-drive';
import { AddToGroupActionSchema } from './policy-actions/add-to-group';
import { ApplySignatureTemplateActionSchema } from './policy-actions/apply-signature-template';
import { ChangeContactSharingActionSchema } from './policy-actions/change-contact-sharing';
import { ChangeCpanelRoleActionSchema } from './policy-actions/change-cPanel-role';
import { ChangeGpanelRoleActionSchema } from './policy-actions/change-gPanel-role';
import { ChangeImapActionSchema } from './policy-actions/change-imap';
import { ChangePopActionSchema } from './policy-actions/change-pop';
import { CreateGmailLabelActionSchema } from './policy-actions/create-gmail-label';
import { DuplicateUserGroupsActionSchema } from './policy-actions/duplicate-user-groups';
import { DynamicGroupAssignmentActionSchema } from './policy-actions/dynamic-group-assignment';
import { SetProfileInfoActionSchema } from './set-profile-info-step';
import { ReassignAliasesActionSchema } from './policy-actions/reassign-alliases';
import { RemoveSpecificSharesActionSchema } from './policy-actions/remove-specific-shares';
import { SetForwardingActionSchema } from './policy-actions/set-forwarding';
import { SetOrganizationActionSchema } from './policy-actions/set-organization';
import { SuspendUserActionSchema } from './policy-actions/suspend-user';
import { SuspendInactiveUsersActionSchema } from './policy-actions/suspend-inactive-users';
import { UpdateMobileDeviceActionSchema } from './policy-actions/update-mobile-devices';
import { RemoveEmailDelegateActionSchema } from './policy-actions/remove-email-delegate';
import { RemoveFromGroupActionSchema } from './policy-actions/remove-from-group';
import { RemoveDomainFindableSharesActionSchema } from './policy-actions/remove-domain-findable-shares';
import { LastLoginActionSchema } from './policy-actions/last-login';

export const ClearPrimaryCalendarSchema = z.object({
  type: z.literal('Clear_Primary_Calendar'),
});

export const DisableTwoFactorSchema = z.object({
  type: z.literal('Disable_Two_Factor'),
});

export const ForcePasswordResetSchema = z.object({
  type: z.literal('Force_Password_Reset'),
});

export const HideFromDirectorySchema = z.object({
  type: z.literal('Hide_From_Directory'),
});

export const RemovePublicSharesSchema = z.object({
  type: z.literal('Remove_Public_Shares'),
});

export const SignOutUserSchema = z.object({
  type: z.literal('Sign_Out_User'),
});

export const UnsuspendUserSchema = z.object({
  type: z.literal('Unsuspend_User'),
});

export const DeleteUserSchema = z.object({
  type: z.literal('Delete_User'),
});

export const PolicyActionSchema = z.discriminatedUnion('type', [
  CreateGmailFilterActionSchema,
  SetProfileInfoActionSchema,
  AddEmailDelegateActionSchema,
  AddSendAsActionSchema,
  AddSharedDriveActionSchema,
  AddToGroupActionSchema,
  ApplySignatureTemplateActionSchema,
  ChangeContactSharingActionSchema,
  ChangeCpanelRoleActionSchema,
  ChangeGpanelRoleActionSchema,
  ChangeImapActionSchema,
  ChangePopActionSchema,
  CreateGmailLabelActionSchema,
  DuplicateUserGroupsActionSchema,
  DynamicGroupAssignmentActionSchema,
  ReassignAliasesActionSchema,
  RemoveSpecificSharesActionSchema,
  SetForwardingActionSchema,
  SetOrganizationActionSchema,
  SuspendUserActionSchema,
  SuspendInactiveUsersActionSchema,
  UpdateMobileDeviceActionSchema,
  RemoveEmailDelegateActionSchema,
  RemoveFromGroupActionSchema,
  RemoveDomainFindableSharesActionSchema,
  LastLoginActionSchema,
  ClearPrimaryCalendarSchema,
  DisableTwoFactorSchema,
  ForcePasswordResetSchema,
  HideFromDirectorySchema,
  RemovePublicSharesSchema,
  SignOutUserSchema,
  UnsuspendUserSchema,
  DeleteUserSchema,
]);

export const PolicyActionModelSchema = z.object({
  actionData: z.record(z.string(), z.any()),
  action: z.string(),
  gmailFilter: FilterModelSchema.nullable().optional(),
  hasRun: z.boolean(),
  id: z.number(),
});

export const SchedulableSchema = z.object({
  id: z.number().optional(),
  emailsToNotify: z.array(z.string()).nullable().optional(),
  emailsToShare: z.array(z.string()).nullable().optional(),
});

// Will separate field based on step along the way
// Note: Will remove optional along the way if removing Partial<> is possible
export const PolicySchema = z.object({
  createdBy: z.string().nullish(),
  createdDate: z.number().catch(new Date().getTime()).optional(),
  lastRunDate: z.number().optional(),
  policyType: z.enum(['standard', 'newUser']).optional(),
  actionHash: z.record(z.string(), PolicyActionModelSchema).optional(),
  runImmediately: z.boolean().optional(),
});

export const PolicyActionFormSchema = z.object({
  actionData: z.array(PolicyActionSchema),
});

export const BasicInformationSchema = z
  .object({
    policyName: z.string().min(1, 'Policy Name is required.').optional(),
    description: z.string().optional(),
    manualRun: z.boolean().optional(),
    scheduled: z.boolean().optional(),
    useRunDate: z.boolean().optional(),
    runDate: z.number().nullable().optional(),
    runWithoutSaving: z.boolean().optional(),
    notifyViaEmail: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.useRunDate) {
      if (!data.runDate || data.runDate <= 0) {
        ctx.addIssue({
          path: ['runDate'],
          message: 'A run date is required.',
          code: 'custom',
        });
      } else if (data.runDate < new Date().setSeconds(0, 0)) {
        ctx.addIssue({
          path: ['runDate'],
          message: 'Run date and time must be in the future.',
          code: 'custom',
        });
      }
    }
  });

export const ActionSchema = z
  .object({
    actions: z.array(z.string()).optional(),
    actionData: z.array(PolicyActionSchema),
  })
  .superRefine((data, ctx) => {
    if (!data.actions || data.actions.length <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Please add at least one action.',
        path: ['actions'],
      });
    }
  });

export const TargetsSchema = z.object({
  sourceType: z
    .enum([
      'All_Users',
      'All_Groups',
      'User',
      'Group',
      'OrgUnit',
      'gPanel_Role',
      'Mobile_Device',
      'Domain',
    ])
    .optional(),
  sources: z.array(z.string()).optional(),
  allSource: z.boolean().optional(),
  targetsExcluded: z.array(z.string()).optional(),
});

export const ScheduleSchema = z
  .object({
    daysOfWeek: z.boolean().optional(),
    scheduled: z.boolean().optional(),
    times: z.array(z.string()).nullable().optional(),
    dates: z.array(z.string()).nullable().optional(),
    scheduleEndDate: z.number().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.scheduled) {
      if (!data.dates || data.dates.length === 0) {
        ctx.addIssue({
          path: ['dates'],
          message: 'At least one date is required.',
          code: 'custom',
        });
      }

      if (!data.times || data.times.length === 0) {
        ctx.addIssue({
          path: ['times'],
          message: 'At least one time is required.',
          code: 'custom',
        });
      }
    }
  });

export const PolicyFormSchema = BasicInformationSchema.and(ActionSchema)
  .and(PolicySchema)
  .and(SchedulableSchema)
  .and(PolicyActionFormSchema)
  .and(TargetsSchema)
  .and(ScheduleSchema)
  .superRefine((data, ctx) => {
    // newUser policies don't show the Targets step, therefore targets won't be populated
    if (data.policyType === 'newUser') {
      return;
    }

    if (!data.allSource && (!data.sources || data.sources.length <= 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Target is required.',
        path: ['sources'],
      });
    }
  });

export type PolicyAction = z.infer<typeof PolicyActionSchema>;
